import type { UserPreferences } from '@/components/onboarding/types';
import type { Language } from '@/i18n/translations';
import type { WorkflowProfile } from '@/lib/workflowTemplates';
import { FALLBACK_BASE_PROMPT } from './layers/baseLayer';
import { buildLanguageLayer } from './layers/languageLayer';
import { buildPersonaHeader } from './layers/personaHeader';
import { buildExperienceLayer } from './layers/experienceLayer';
import { buildRoleLayer } from './layers/roleLayer';
import { buildUseCaseLayer } from './layers/useCaseLayer';
import { buildInterestsLayer } from './layers/interestsLayer';
import { buildTransientLayer } from './layers/transientLayer';

export interface BuildSystemPromptArgs {
  /** Persistent persona from onboarding. null = anonymous or skipped. */
  preferences: UserPreferences | null;
  /** Transient per-workflow hone (templateId + q1 + q2). */
  workflowProfile: WorkflowProfile | null;
  /** UI language. */
  lang: Language;
  /** Optional base prompt override (defaults to FLOW_WEAVER fallback). */
  basePrompt?: string;
}

export interface ComposedPrompt {
  /** Stable prefix suitable for prompt-caching (layers 1–7). */
  cacheablePrefix: string;
  /** Volatile tail — NOT cached (layer 8). */
  transientTail: string;
  /** Full concatenation for LLMs that don't support cache_control. */
  full: string;
}

function joinSections(...parts: Array<string | undefined | null>): string {
  return parts.filter((p): p is string => !!p && p.length > 0).join('\n\n');
}

/**
 * Composes the layered AI system prompt from onboarding preferences +
 * per-workflow honing. Pure function — fully deterministic so we can
 * snapshot-test it.
 *
 * Layer order (documented in plan DEL 13):
 *   1. BASE_PROMPT
 *   2. LANGUAGE_DIRECTIVE
 *   3. PERSONA_HEADER
 *   4. EXPERIENCE_LAYER
 *   5. ROLE_LAYER
 *   6. USECASE_LAYER (+ tool vocabulary)
 *   7. INTERESTS_LAYER            ← cache boundary
 *   8. TRANSIENT_HONING          ← volatile, excluded from cache
 */
export function buildSystemPrompt(args: BuildSystemPromptArgs): ComposedPrompt {
  const { preferences, workflowProfile, lang, basePrompt } = args;

  const base = basePrompt ?? FALLBACK_BASE_PROMPT;
  const language = buildLanguageLayer(lang);

  const cacheableParts = preferences
    ? [
        base,
        language,
        buildPersonaHeader(preferences, lang),
        buildExperienceLayer(preferences.experience, lang),
        buildRoleLayer(preferences.role, lang),
        buildUseCaseLayer(preferences.useCase, lang),
        buildInterestsLayer(preferences.interests, lang),
      ]
    : [base, language];

  const cacheablePrefix = joinSections(...cacheableParts);
  const transientTail = buildTransientLayer(workflowProfile, lang);
  const full = joinSections(cacheablePrefix, transientTail);

  return { cacheablePrefix, transientTail, full };
}
